/**
 * Simple in-memory API response cache with TTL (Time-To-Live).
 * 
 * Purpose: Prevents repeated API calls when navigating between pages.
 * Data is stored in memory and survives component unmount/remount,
 * but is cleared on full page refresh (which is the desired behavior).
 * 
 * Default TTL: 5 minutes
 */

const cache = new Map();

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Get cached data for a given key.
 * Returns null if key doesn't exist or if TTL has expired.
 * 
 * @param {string} key - Cache key
 * @returns {any|null} - Cached data or null
 */
export const getCachedData = (key) => {
  if (!cache.has(key)) return null;

  const entry = cache.get(key);
  return entry.data;
};

/**
 * Store data in cache with a TTL.
 * 
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 * @param {number} [ttl] - Time-to-live in milliseconds (default: 5 minutes)
 */
export const setCachedData = (key, data, ttl = DEFAULT_TTL) => {
  cache.set(key, {
    data,
    timestamp: Date.now(),
    ttl,
  });
};

/**
 * Remove a specific key from cache.
 * Useful after mutations (e.g., submitting a form) to force fresh data.
 * 
 * @param {string} key - Cache key to remove
 */
export const clearCache = (key) => {
  cache.delete(key);
};

/**
 * Remove all entries matching a prefix.
 * e.g., clearCacheByPrefix("homework_") clears all homework-related cache.
 * 
 * @param {string} prefix - Key prefix to match
 */
export const clearCacheByPrefix = (prefix) => {
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
};

/**
 * Clear the entire cache.
 */
export const clearAllCache = () => {
  cache.clear();
};
